import { Component, Input, TemplateRef, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { FixturePricingState } from '@cds/betting-offer/add-ons';
import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { Meeting } from '@cds/betting-offer/tags';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { HorsesMeetingAllRaceViewModel, MeetingModel, WinGameFixturePreview } from '@frontend/sports/racing/feature/models';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DeviceService, IntlService } from '@frontend/vanilla/core';

import { convertDayOfRaceToUrlParam } from '../../../../horse-racing-shared/helpers';
import { TrackNavArea } from '../../../../navigation/navigation-tracking.model';
import { SubNavigationItem } from '../../../../sub-navigation/sub-navigation.models';

export abstract class RacingMeetingAllComponent extends OnRouteResolve<MeetingModel | undefined> {
    protected model?: MeetingModel;
    meeting?: Meeting;
    dayOfRace?: DayOfRaceDate;
    tabs: SubNavigationItem[] = [];
    races: HorsesMeetingAllRaceViewModel[] = [];
    pricingStates = {
        Resulted: FixturePricingState.Resulted,
        Priced: FixturePricingState.Priced,
        RaceOff: FixturePricingState.RaceOff,
        None: FixturePricingState.None,
    };

    constructor(
        public sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        private intlService: IntlService,
        route: ActivatedRoute,
        private redirect: RedirectHelperService,
    ) {
        super(route);
    }

    abstract sportName(): SportConstant;

    onRouteResolved(model?: MeetingModel): void {
        this.model = model;
        this.races = this.getRaces();
        this.dayOfRace = this.model?.day;
        this.meeting = this.model?.fixtures[0]?.meeting;
        const raceId = (this.route.snapshot.params.raceId && this.route.snapshot.params.raceId.toString()) || '';
        if (model) {
            const all = this.buildAllTab(raceId, model);
            const raceTabs = model.fixtures.map((f) => this.fixtureToTab(f, raceId, model));
            this.tabs = [all, ...raceTabs];
        }
    }

    onFixtureClick(fixture: WinGameFixturePreview): void {}

    private raceName(fixture: WinGameFixturePreview): string {
        const handicap = fixture.addons.handicap ? ` ${this.sitecore.horseRacing.common.Handicap}` : '';

        return `${fixture.addons.distance || ''}${handicap} ${fixture.addons.courseType || ''}`;
    }

    private getFixtureUrl(fixture: WinGameFixturePreview): string {
        return this.urlHelper.getRacingUrlByFixture(fixture.sport.id, fixture);
    }

    private getRaces(): HorsesMeetingAllRaceViewModel[] {
        if (!this.model) {
            return [];
        }

        return this.model.fixtures.map((f: WinGameFixturePreview) => ({
            fixture: f,
            pricingState: f.addons.pricingState || FixturePricingState.None,
            fixtureUrl: this.getFixtureUrl(f),
            startDate: f.startDate,
            raceName: this.raceName(f),
        }));
    }

    selected(activeTabId: string | number): void {
        let selectedTab = this.tabs?.find((t) => t.id.toString() === activeTabId.toString());

        if (!selectedTab?.url) {
            return;
        }
        if (selectedTab) {
            this.tabs.forEach((tab) => (tab.active = tab.id === selectedTab?.id));
        }
        this.redirect.goToPage(selectedTab.url, selectedTab.ignoreBrowserHistory);
    }

    get active(): SubNavigationItem {
        return (this.tabs && this.tabs.filter((tab) => tab.active).pop()) ?? this.tabs[0];
    }

    private buildAllTab(raceId: string, model: MeetingModel): SubNavigationItem {
        return {
            id: 0,
            title: this.sitecore.horseRacing.common.All,
            url: this.urlHelper.getRaceUrl(this.sportName(), model.meeting, convertDayOfRaceToUrlParam(model.day)),
            active: !raceId,
            ignoreBrowserHistory: true,
        };
    }

    private fixtureToTab(fixture: WinGameFixturePreview, raceId: string, model: MeetingModel): SubNavigationItem {
        const resulted = fixture.addons.pricingState === FixturePricingState.Resulted;

        return {
            id: +fixture.fixtureId,
            title: this.intlService.formatDate(fixture.startDate, 'HH:mm') || '',
            url: this.urlHelper.getRacingUrlByFixture(fixture.sport.id, fixture),
            active: fixture.id === raceId,
            icon: resulted ? 'sports-race-resulted' : '',
            classes: resulted ? 'race-resulted' : '',
            ignoreBrowserHistory: true,
        };
    }
}

@HooksWireup()
@Component({
    selector: 'ms-greyhounds-meeting-all',
    templateUrl: './greyhounds-meeting-all.component.html',
    styleUrl: './greyhounds-meeting-all.component.scss',
    standalone: false,
})
export class GreyhoundsMeetingAllComponent extends RacingMeetingAllComponent {
    isMobile: boolean;
    @Input() iconTemplate: TemplateRef<any>;
    trackingValue = signal<string>('M2_ListItemlinks');
    cleanedAfterTrack = signal(false);

    constructor(
        sitecore: Sitecore,
        urlHelper: UrlHelperService,
        intlService: IntlService,
        route: ActivatedRoute,
        private deviceService: DeviceService,
        private redirectHelper: RedirectHelperService,
        private trackingService: TrackingService,
    ) {
        super(sitecore, urlHelper, intlService, route, redirectHelper);
        this.isMobile = this.deviceService.isMobile;
    }

    goBack = (): void => {
        this.trackingService.track(trackingConstants.EVENT_NAVIGATION_MENUS, {
            [trackingConstants.PAGE_NAVIGATION_MENUS]: `${TrackNavArea.NavBreadcrumb}_Back`,
        });
        this.redirectHelper.goBack();
    };

    sportName(): SportConstant {
        return SportConstant.Greyhounds;
    }

    get meetingName() {
        return this.meeting?.name.value;
    }

    shouldShowStartSlot(race: any): boolean {
        return [this.pricingStates.Resulted, this.pricingStates.Priced, this.pricingStates.RaceOff].includes(race.pricingState);
    }
}
